import React from 'react';

import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { getFormattedAddress } from '@bringg/react-components';
import { PrivilegeTypes, Task } from '@bringg/types';

import { tooltipProvider } from '../tooltip-render-provider';
import { Link } from 'bringg-web/components';
import TaskStatusLabel from './task-status-label';
import TaskTooltipTimes from './task-tooltip-times';
import TaskType from './task-type';
import useTaskTooltipTranslation from './use-task-tooltip-translation';
import { getActionableWaypoint } from 'bringg-web/stores/tasks/task-utils';
import useTimeFormat from 'bringg-web/hooks/use-time-format';
import { useStores } from 'bringg-web/recipes';
import { useHasAccess } from 'bringg-web/utils/privileges';

import styles from '../tooltip.module.scss';

interface Props {
	task: Task;
	dataTestId: string;
	isMapTooltip?: boolean;
}

const TasksTooltipContent = ({ task, dataTestId, isMapTooltip }: Props) => {
	const {
		merchantConfigurationsStore: { configuration }
	} = useStores();
	const { priority, id, to, address, noAddress, timeWindow, weight, items, skills, orderPage } =
		useTaskTooltipTranslation();
	const hourFormat = useTimeFormat();

	const getWeightValue = (): string => {
		const itemsCount = task.aggregations?.inventory_total_quantity;
		let weightValue = task.aggregations?.max_weight && `${task.aggregations.max_weight}`;

		if (itemsCount && weightValue) {
			weightValue += ` / ${itemsCount} ${items}`;
		}
		return weightValue;
	};

	const privilegeToViewTask = useHasAccess(PrivilegeTypes.VIEW_TASK);

	const wayPoint = task && getActionableWaypoint(task);
	const wpEarlierTime = wayPoint && (wayPoint.no_earlier_than || wayPoint.first_attempt_promise_no_earlier_than);
	const wpLaterTime = wayPoint && (wayPoint.no_later_than || wayPoint.first_attempt_promise_no_later_than);
	const customer = wayPoint?.customer_id && getRootEnv().dashboardSdk.sdk.v2.customers.get(wayPoint.customer_id);

	return (
		<div className={styles.content} data-test-id={dataTestId}>
			<div className={styles.header}>
				{tooltipProvider.renderText(priority, `${task.priority}`)}
				{privilegeToViewTask && (
					<Link to={`${isMapTooltip ? '#/' : ''}history/${task.id}`} openInNewTab>
						{orderPage} <BringgIcon iconName={BringgFontIcons.OpenInNew} />
					</Link>
				)}
			</div>
			<TaskStatusLabel task={task} />
			<TaskTooltipTimes task={task} wayPoint={wayPoint} />
			{tooltipProvider.renderTime(hourFormat, timeWindow, wpEarlierTime, wpLaterTime)}
			{tooltipProvider.renderText(id, `${task.external_id || task.id}`)}
			{tooltipProvider.renderText(to, customer?.name)}
			{tooltipProvider.renderText(
				address,
				getFormattedAddress(wayPoint, configuration.address_format) || noAddress
			)}
			{tooltipProvider.renderText(weight, getWeightValue())}
			<TaskType pickupDropoffOptions={wayPoint.pickup_dropoff_option} />
			<div>
				{task.required_skills?.length
					? tooltipProvider.renderText(skills, task.required_skills.join(', '))
					: null}
			</div>
		</div>
	);
};

export default TasksTooltipContent;
